import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  FiCalendar,
  FiInfo,
  FiCheckCircle,
  FiRefreshCw,
  FiUsers,
} from 'react-icons/fi';

import { Text, textVariantsEnum, fontWeightEnum } from '@components/Text';
import { Button, buttonVariants } from '@components/Button';
import { Callout, calloutVariants } from '@components/Callout';
import { CounterStepper } from '@components/CounterStepper';
import {
  StatHighlightCard,
  statHighlightTones,
} from '@components/StatHighlightCard';
import { IconBadge, iconBadgeSizes } from '@components/IconBadge';
import { addNotification, notificationStates } from '@components/Notification';
import { useLeavePolicy } from '@/hooks/useLeavePolicy';
import companyService from '@/services/company';

import { SettingsPanelHeader } from './SettingsPanelHeader.component';

import styles from './LeaveAttendanceSection.module.scss';

const FEATURE_ROWS = [
  {
    key: 'reset',
    Icon: FiRefreshCw,
    iconBg: 'indigo-100',
    iconColor: 'indigo-600',
    title: 'Balance resets every year',
    description: 'Leave balance will be reset on 1st January every year.',
  },
  {
    key: 'appliesToAll',
    Icon: FiUsers,
    iconBg: 'indigo-100',
    iconColor: 'indigo-600',
    title: 'Applies to all employees',
    description: 'This policy is applicable to every employee in the company.',
  },
  {
    key: 'noCarryForward',
    Icon: FiCalendar,
    iconBg: 'orange-100',
    iconColor: 'orange-600',
    title: 'No carry forward',
    description:
      'Unused leave days will not be carried forward to the next year.',
  },
];

export const LeaveAttendanceSection = () => {
  const userProfile = useSelector((state) => state.auth.profile);
  const isAdmin = userProfile?.activeCompany?.role === 'Admin';
  const { paidLeaveAllocation } = useLeavePolicy();

  const [leaveDays, setLeaveDays] = useState(25);
  const [saving, setSaving] = useState(false);

  // Load the company's saved policy into the editable field whenever it
  // changes (initial fetch, or a live update from another admin's save).
  useEffect(() => {
    setLeaveDays(paidLeaveAllocation);
  }, [paidLeaveAllocation]);

  const handleSave = async () => {
    if (!isAdmin) {
      addNotification({
        message:
          'Access denied: Only administrators can configure leave policy',
        level: notificationStates.error,
      });
      return;
    }

    setSaving(true);
    try {
      const result = await companyService.updateLeavePolicy({
        companyId: userProfile.activeCompany.companyId,
        paidLeaveAllocation: leaveDays,
        updatedBy: userProfile.id,
      });
      if (!result.success) throw new Error(result.error);

      addNotification({
        message: 'Leave policy updated successfully',
        level: notificationStates.success,
      });
    } catch (error) {
      console.error('Error updating leave policy:', error);
      addNotification({
        message: 'Failed to update leave policy',
        level: notificationStates.error,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <SettingsPanelHeader
        icon={<FiCalendar size={20} color="var(--indigo-600)" />}
        title="Leave & Attendance"
        description="Configure your company's annual leave policy"
      />

      <div className={styles.bodyGrid}>
        <div className={styles.leftColumn}>
          <div className={styles.leftTop}>
            <div className={styles.sectionHeading}>
              <Text
                variant={textVariantsEnum.Text_md}
                fontWeight={fontWeightEnum.Semibold}
                color="gray-900"
              >
                Annual Leave Policy
              </Text>
              <Text
                variant={textVariantsEnum.Text_sm}
                fontWeight={fontWeightEnum.Normal}
                color="gray-600"
              >
                Set the default number of paid leave days allocated to every
                employee each year.
              </Text>
            </div>

            <CounterStepper
              value={leaveDays}
              onChange={setLeaveDays}
              min={0}
              max={365}
              unitLabel="Days / Year"
            />

            <Callout variant={calloutVariants.info} icon={<FiInfo size={14} />}>
              Employees joining mid-year will receive a prorated balance.
            </Callout>
          </div>

          <div className={styles.leftBottom}>
            <Button
              text="Save Policy"
              variant={buttonVariants.primary}
              icon={<FiCheckCircle size={16} />}
              onClick={handleSave}
              loading={saving}
            />
            <Text
              variant={textVariantsEnum.Text_xs}
              fontWeight={fontWeightEnum.Normal}
              color="gray-500"
            >
              Changes will apply to all employees.
            </Text>
          </div>
        </div>

        <div className={styles.rightColumn}>
          <div className={styles.sectionHeading}>
            <Text
              variant={textVariantsEnum.Text_md}
              fontWeight={fontWeightEnum.Semibold}
              color="gray-900"
            >
              Policy Preview
            </Text>
            <Text
              variant={textVariantsEnum.Text_sm}
              fontWeight={fontWeightEnum.Normal}
              color="gray-600"
            >
              Here&apos;s how the leave policy works for your company.
            </Text>
          </div>

          <StatHighlightCard
            value={leaveDays}
            label="Paid Leave Days / Year"
            sublabel="Every employee receives"
            tone={statHighlightTones.success}
          />

          <div className={styles.featureList}>
            {FEATURE_ROWS.map(
              ({ key, Icon, iconBg, iconColor, title, description }) => (
                <div className={styles.featureRow} key={key}>
                  <IconBadge
                    size={iconBadgeSizes.sm}
                    bg={iconBg}
                    icon={<Icon size={16} color={`var(--${iconColor})`} />}
                  />
                  <div className={styles.featureText}>
                    <Text
                      variant={textVariantsEnum.Text_sm}
                      fontWeight={fontWeightEnum.Semibold}
                      color="gray-900"
                    >
                      {title}
                    </Text>
                    <Text
                      variant={textVariantsEnum.Text_xs}
                      fontWeight={fontWeightEnum.Normal}
                      color="gray-600"
                    >
                      {description}
                    </Text>
                  </div>
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </>
  );
};

LeaveAttendanceSection.displayName = 'LeaveAttendanceSection';
